"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";

/** Content is visible in server HTML; offscreen reveals enhance it after hydration. */
type RevealProps = {
  children: React.ReactNode;
  /** ms delay before the transition starts — use to stagger siblings */
  delay?: number;
  /** vertical travel distance in px (default 16) */
  y?: number;
  className?: string;
};

export const Reveal: React.FC<RevealProps> = ({
  children,
  delay = 0,
  y = 16,
  className,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [phase, setPhase] = useState<"visible" | "waiting" | "revealing">("visible");
  const finish = useCallback(() => {
    observerRef.current?.disconnect();
    setPhase("visible");
  }, []);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotion = () => {
      if (media.matches) finish();
    };
    media.addEventListener("change", updateMotion);

    const node = ref.current;
    if (node && !media.matches && "IntersectionObserver" in window) {
      const rect = node.getBoundingClientRect();
      // Keep initial viewport content visible, including when hydration is late.
      if (rect.top >= window.innerHeight || rect.bottom <= 0) {
        setPhase("waiting");
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              setPhase(media.matches ? "visible" : "revealing");
              observer.disconnect();
            }
          },
          { threshold: 0, rootMargin: "0px 0px -8% 0px" }
        );
        observerRef.current = observer;
        observer.observe(node);
      }
    }

    return () => {
      observerRef.current?.disconnect();
      media.removeEventListener("change", updateMotion);
    };
  }, [finish]);

  useEffect(() => {
    if (phase !== "revealing") return;
    // Also release the compositing hint when a transition event is interrupted.
    const timer = window.setTimeout(finish, Math.max(0, delay) + 700);
    return () => window.clearTimeout(timer);
  }, [phase, delay, finish]);

  return (
    <div
      ref={ref}
      className={className}
      onFocusCapture={finish}
      onTransitionEnd={(event) => {
        if (event.target === event.currentTarget && event.propertyName === "opacity") finish();
      }}
      style={phase === "visible" ? undefined : {
        opacity: phase === "revealing" ? 1 : 0,
        transform: phase === "revealing" ? "translateY(0)" : `translateY(${y}px)`,
        ...(phase === "revealing" ? {
          transition: "opacity 0.6s ease-out, transform 0.6s ease-out",
          transitionDelay: `${Math.max(0, delay)}ms`,
          willChange: "opacity, transform",
        } : {}),
      }}
    >
      {children}
    </div>
  );
};
